import {PositionedBox, Text} from 'exsportia-components'
import {useCallback, useEffect, useRef, useState} from 'react'

import {theme} from '../theme/theme'
import {useLocale} from './i18n'
type UseTimeoutProps = {
  callback?: () => null;
  delay?: number;
  isDisabled?: boolean;
  // Idle-reset is right for "the user walked away, go home" but wrong for
  // "the payment is done, go back". On a touchscreen every tap synthesises a
  // mousemove, so an idle timer restarts on each touch and the return never
  // lands. Pass false for a fixed countdown that activity cannot postpone.
  resetOnActivity?: boolean;
}
export const useTimeout = ({
  callback = () => null,
  delay = 15000,
  isDisabled = false,
  resetOnActivity = true,
}: UseTimeoutProps = {}) => {
  const savedCallback = useRef<() => void>(() => null)
  const timeRemainsRef = useRef<number>(delay)
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null)
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null)
  const {l} = useLocale()
  const [goBackMessage, setGoBackMessage] = useState(null)

  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  const clearExistingTimeouts = useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current)
      timeoutIdRef.current = null
    }
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current)
      intervalIdRef.current = null
    }
  }, [])

  const resetTimer = useCallback(() => {
    clearExistingTimeouts()
    timeRemainsRef.current = delay

    timeoutIdRef.current = setTimeout(() => {
      savedCallback.current()
    }, delay)

    intervalIdRef.current = setInterval(() => {
      timeRemainsRef.current -= 1000
      if (timeRemainsRef.current <= 0) {
        clearExistingTimeouts()
      }
    }, 1000)
  }, [delay, clearExistingTimeouts])

  useEffect(() => {
    if (isDisabled || !delay) {
 return
}

    resetTimer()
    if (!resetOnActivity) {
      return clearExistingTimeouts
    }
    const eventHandler = () => {
      resetTimer()
    }
    const listeners = ['mousemove', 'keypress'] // Add more event types as needed
    listeners.forEach((event) => {
      window.addEventListener(event, eventHandler)
    })

    return () => {
      listeners.forEach((event) => {
        window.removeEventListener(event, eventHandler)
      })
      clearExistingTimeouts()
    }
  }, [delay, resetTimer, isDisabled, resetOnActivity, clearExistingTimeouts])

  useEffect(() => {
    const intervalId = setInterval(() => {
      const timeRemainsInSeconds = Math.ceil(timeRemainsRef.current / 1000)
      if (timeRemainsInSeconds <= 6) {
        const Component = (<PositionedBox
          zIndex='3'
          position='sticky'
          bg={theme.colors.pageBg}
          p={theme.spaces.multiplier}
          my={theme.spaces.multiplier2x}
          top={theme.spaces.multiplier10x}
          borderRadius={theme.spaces.multiplier}
        >
          <Text settings={{color: theme.colors['text-secondary']}}>
            {l('redirectToTheHome')} {timeRemainsInSeconds}...
          </Text>
        </PositionedBox>)
        // @ts-ignore
        setGoBackMessage( Component )
      } else {
        setGoBackMessage(null)
      }
    }, 1000)

    return () => clearInterval(intervalId)
  }, [l])

  if (isDisabled) {
 return {}
}

  return {timeLeft: timeRemainsRef.current, timeRemains: timeRemainsRef.current, goBackMessage}
}
